/**
 * Dashboard's Component Inspector boundary — pure gate logic, zero
 * `@freightsimple/dev-tools` imports.
 *
 * For non-employees we render children straight through with no inspector
 * machinery at all. For employees we lazy-load the heavy
 * `EmployeeInspectorMount` chunk (which is where the package import + the
 * `setInspectorSourceMap` / `setInspectorConfig` calls live). Customers
 * never download that chunk, so the ~1200-line inspector + dev-tools
 * package + source-map glob stay out of their bundle entirely.
 *
 * Suspense fallback renders `children` directly so the wrapping is
 * invisible during the (tiny) lazy fetch.
 */

import { lazy, Suspense, type ReactNode } from "react";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { showDeveloperAffordances } from "@/Helpers/developerMode";

const EmployeeInspectorMount = lazy(() => import("./EmployeeInspectorMount"));

export function DashboardInspector({ children }: { children: ReactNode }) {
  const { user } = useAuthentication();
  if (!showDeveloperAffordances(user?.email)) return <>{children}</>;
  return (
    <Suspense fallback={<>{children}</>}>
      <EmployeeInspectorMount>{children}</EmployeeInspectorMount>
    </Suspense>
  );
}
